// 권한 관리 화면용 카탈로그 표시 도우미 (2026-10-30 결정).
// - hidden 키(쓰는 어드민 화면이 없는 키)는 권한 관리 UI 어디에도 보이지 않는다.
// - 권한 키마다 그 키를 쓰는 실제 화면으로 가는 링크를 보여준다. screens 는 API 가 App.jsx 라우트 경로 그대로 준다.
// 이전 API(screens/hidden 없음)면 hidden 아님 + 링크 없음으로 다룬다.
import { SIDEBAR_MENU } from "./routeAccess";

// App.jsx 의 <Route path="..."> 목록(로그인 "/" 제외). 라우트를 추가하면 함께 고친다 — App.jsx 와 같은지 테스트가 확인한다.
export const APP_ROUTE_PATHS = [
  "/dashboard",
  "/blog-analysis-registry",
  "/users",
  "/users/detail/:userId",
  "/users/create",
  "/users/password",
  "/rewards/users",
  "/rewards/settlement",
  "/rewards/users/detail",
  "/ads/campaign/list",
  "/ads/campaign/trash",
  "/ads/campaign/verify",
  "/ads/products",
  "/ads/product/write",
  "/ads/product/detail",
  "/ads/invoice",
  "/ads/reviewDelete/list",
  "/ads/newsRelease/categories",
  "/ads/newsRelease/category/write",
  "/ads/newsRelease/category/detail",
  "/ads/newsRelease/management",
  "/ads/blog/management",
  "/ads/momcafe-carrot/management",
  "/ads/experience-reviewer/management",
  "/ads/branding-package/management",
  "/ads/logo-design/management",
  "/ads/add-kakao-friend/management",
  "/ads/follow-on-daangn/management",
  "/ads/biz-profile/management",
  "/ads/review/management",
  "/ads/shopping/management",
  "/ads/partner-cafe/management",
  "/ads/tv-ad/management",
  "/ads/tenant-brokerage-fee/management",
  "/ads/revenue-analysis",
  "/ads/settlement/referral-management",
  "/ads/settlement/campaign-history",
  "/ads/settlement/settlement-management",
  "/reward-admin/campaign-management",
  "/reward-admin/consumption-volume",
  "/wallet/dashboard",
  "/wallet/charge",
  "/wallet/payment",
  "/wallet/cancel",
  "/wallet/withdrawal",
  "/wallet/user",
  "/wallet/user/:userId",
  "/wallet/policy",
  "/access/permissions",
  "/access/admins",
  "/access/roles",
  "/access/audit-logs",
];

// 사이드바 메뉴에 없는 화면의 이름. entry 가 있으면 직접 열면 빈 화면이 되는(location.state 로 대상을 받는) 상세 화면이라
// 진입하는 목록 화면으로 보낸다.
export const EXTRA_SCREENS = {
  "/ads/product/write": { label: "[상품]상품 등록" },
  "/ads/product/detail": { label: "[상품]상품 상세", entry: "/ads/products" },
  "/ads/newsRelease/category/write": { label: "[언론배포]카테고리 등록" },
  "/ads/newsRelease/category/detail": { label: "[언론배포]카테고리 상세", entry: "/ads/newsRelease/categories" },
  "/rewards/users/detail": { label: "리워드 회원 상세", entry: "/rewards/users" },
  "/wallet/user": { label: "사용자 지갑 상세" },
  "/ads/add-kakao-friend/management": { label: "[카카오 친구추가]신청 관리" },
};

const KNOWN_ROUTES = new Set(APP_ROUTE_PATHS);

const normalize = (path) => {
  const trimmed = typeof path === "string" ? path.trim() : "";
  return trimmed ? trimmed.replace(/\/+$/, "") || "/" : "";
};
const isParamRoute = (path) => path.split("/").some((seg) => seg.startsWith(":"));

// 메뉴 경로 → 이름. 메뉴 항목 이름이 겹치면("회원 목록" 등) "그룹 · 항목"으로 구분한다.
const MENU_LABELS = (() => {
  const entries = SIDEBAR_MENU.flatMap((menu) =>
    menu.items
      ? menu.items.map((item) => ({ path: item.to, label: item.label, group: menu.label }))
      : [{ path: menu.to, label: menu.label }],
  );
  const count = entries.reduce((acc, e) => acc.set(e.label, (acc.get(e.label) ?? 0) + 1), new Map());
  return new Map(
    entries.map((e) => [e.path, count.get(e.label) > 1 && e.group ? `${e.group} · ${e.label}` : e.label]),
  );
})();

/** 화면 경로의 사람이 읽는 이름. 메뉴 이름 → 보조 이름 → 경로 그대로. */
export function screenLabel(path) {
  const p = normalize(path);
  return MENU_LABELS.get(p) ?? EXTRA_SCREENS[p]?.label ?? p;
}

/**
 * 화면 경로 하나를 링크로 바꾼다 → { to: string|null, label, source }.
 * - 파라미터 없는 알려진 라우트: 그 경로(진입 화면이 정해진 상세 화면은 진입 화면).
 * - :param 라우트: 파라미터 앞에서부터 위로 올라가며 App.jsx 에 있는 가장 가까운 상위 라우트. 없으면 to=null(링크 없음).
 * - App.jsx 에 없는 경로: to=null.
 */
export function resolveScreenLink(path) {
  const source = normalize(path);
  if (!source) return null;

  if (!isParamRoute(source)) {
    if (!KNOWN_ROUTES.has(source)) return { to: null, label: screenLabel(source), source };
    const to = EXTRA_SCREENS[source]?.entry ?? source;
    return { to, label: screenLabel(to), source };
  }

  const segments = source.split("/").filter(Boolean);
  const firstParam = segments.findIndex((seg) => seg.startsWith(":"));
  for (let i = firstParam; i > 0; i -= 1) {
    const parent = `/${segments.slice(0, i).join("/")}`;
    if (KNOWN_ROUTES.has(parent)) return { ...resolveScreenLink(parent), source };
  }
  return { to: null, label: screenLabel(source), source };
}

/**
 * screens(경로 배열) → 순서를 지키고 중복을 없앤 링크 목록 [{ to, label, sources }].
 * 매핑 뒤 같은 곳으로 가는 링크(to 가 같거나, 링크 없는 항목은 이름이 같음)는 하나로 합친다. 배열이 아니면 [].
 */
export function resolveScreenLinks(screens) {
  if (!Array.isArray(screens)) return [];
  const result = [];
  const byId = new Map();
  screens.forEach((path) => {
    const link = resolveScreenLink(path);
    if (!link) return;
    const id = link.to ? `to:${link.to}` : `text:${link.label}`;
    const existing = byId.get(id);
    if (existing) {
      if (!existing.sources.includes(link.source)) existing.sources.push(link.source);
      return;
    }
    const item = { to: link.to, label: link.label, sources: [link.source] };
    byId.set(id, item);
    result.push(item);
  });
  return result;
}

/**
 * API 카탈로그 → { catalog: 보이는 키만 남긴 도메인 그룹(키가 모두 숨김인 도메인은 제외), hiddenKeys: Set, permissionByKey: Map }.
 * hidden 이 true 인 키만 숨긴다(필드가 없으면 보임). screens 가 배열이 아니면 [] 로 채운다.
 */
export function splitCatalog(rawCatalog) {
  const hiddenKeys = new Set();
  const permissionByKey = new Map();
  const catalog = [];
  (Array.isArray(rawCatalog) ? rawCatalog : []).forEach((group) => {
    const visible = [];
    (group?.permissions ?? []).forEach((p) => {
      const permission = { ...p, screens: Array.isArray(p.screens) ? p.screens : [], hidden: p.hidden === true };
      if (permission.hidden) {
        hiddenKeys.add(permission.key);
        return;
      }
      permissionByKey.set(permission.key, permission);
      visible.push(permission);
    });
    if (visible.length > 0) catalog.push({ ...group, permissions: visible });
  });
  return { catalog, hiddenKeys, permissionByKey };
}
